
import { createAsyncActionCreator, IAsyncActionCreator } from '../store/actions/createActionCreator';
import { makeObject, mapObject, pickObject, assign } from '../utilities/objects';
import { subtract, isSubset } from '../utilities/Graft';
import { Signal } from '../utilities/async/Signal';
import { KeyFacet, stringPart } from '../models/KeyFacet';
import { Facet, IFaceted, pack, IFacet } from '../models/Facet';
import {
  IQueuedOperation,
  IOperationPayload,
  getOperationTypeKey,
  getOperationState,
  editQueue,
  OperationTypeKey,
  startOperation,
  ItemOperationError,
  noRegisteredHandlerErrorFacet
} from '../store/actions/implementOperation';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { updateItems } from '../store/reducers/itemMapReducer';
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';
import { resolvedFacet, DemandResolution, errorFacet } from '../facets/DemandFacets';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';
import { asOnceOnly } from '../store/middleware/RunOnceMiddleware';
import { preferredKeyFacetFacet, operationQueueHandlerFacet } from '../facets/OperationFacets';
import { waitForAll } from '../utilities/async/all';
import { getItemsOperation } from './getItemsOperation';
import { handleError } from './handleError';

const supportedFacetsItemKeyFacet = new KeyFacet<{
  keyFacetId: string;
}>('supportedFacets', {
  keyFacetId: stringPart()
});

const supportedFacetsFacet = new Facet<{
  demands: IFaceted<boolean>;
}>('supportedFacets');

export { getItemsOperation };

/**
 * @public
 */
export interface IGetItemsErrorProps extends ICustomErrorProps {
  facets?: IFaceted;
}

/**
 * @public
 */
export class GetItemsError extends CustomError {
  public readonly facets: IFaceted;

  constructor(props: IGetItemsErrorProps) {
    super(props);

    const {
      facets = pack({}, {})
    } = props;

    this.facets = facets;
  }
}

function isUnhandledOperationError(error: Error): boolean {
  return error instanceof ItemOperationError && !!noRegisteredHandlerErrorFacet.unpack(error.facets);
}

/**
 * Implementation for `enqueueOperations` which only appends each incoming
 * operation to the queue if its demands are not met by any existing
 * operations, and then only emits an operation with the remaining demands.
 */
function enqueueGetItemsOperations<TKey extends object, TOptions extends object>(
  currentOperations: IQueuedOperation<TKey, TOptions>[],
  newOperations: IQueuedOperation<TKey, TOptions>[]
): IQueuedOperation<TKey, TOptions>[] {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  for (const {
    itemKey,
    startSignal,
    completeSignal,
    options: newOptions
  } of newOperations) {
    let remainingOptions = newOptions;

    let matchingOperationCount = 0;

    const startedWork: Promise<void>[] = [];
    const completedWork: Promise<void>[] = [];

    for (const {
      itemKey: currentItemKey,
      startSignal: currentStartSignal,
      completeSignal: currentCompleteSignal,
      options: currentOptions
    } of currentOperations) {
      if (itemKey === currentItemKey) {
        matchingOperationCount++;
        const previousRemainingOptions = remainingOptions;

        // Remove anything in the new operation that an existing operation already covers.
        remainingOptions = subtract(remainingOptions, currentOptions);

        if (!isSubset(remainingOptions, previousRemainingOptions)) {
          completedWork.push(currentCompleteSignal.promise);
          startedWork.push(currentStartSignal.promise);
        }
      }
    }

    if (Object.keys(remainingOptions).length > 0 || matchingOperationCount === 0) {
      // The new operation has some options which are not met by an existing operation.

      if (!newCurrentOperations) {
        newCurrentOperations = [...currentOperations];
      }

      // TODO Insert a new operation per demanded facet, not an aggregate of all of them.

      const newStartSignal = new Signal<void>();
      const newCompleteSignal = new Signal<void>();
      newCompleteSignal.promise.then(newStartSignal.resolve, newStartSignal.reject);

      startedWork.push(newStartSignal.promise);
      completedWork.push(newCompleteSignal.promise);

      newCurrentOperations.push({
        itemKey: itemKey,
        startSignal: newStartSignal,
        completeSignal: newCompleteSignal,
        options: remainingOptions
      });
    }

    // When any of the work is started, consider the entire request as started.
    Promise.race(startedWork).then(() => startSignal.resolve(), startSignal.reject);

    // When all of the work has completed, consider the entire request as completed.
    waitForAll(completedWork).then(() => completeSignal.resolve(), completeSignal.reject);
  }

  return newCurrentOperations || currentOperations;
}

/**
 * Given a queue of pending operations as well as a completed batch operation,
 * produces a revised queue in which the remaining pending operations
 * have no intersection with the completed operation.
 */
function cleanupResolvedGetItemsOperations<TKey extends object>(
  currentOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[],
  completedOperations: IOperationPayload<typeof getItemsOperation.type>
): {
    newQueue: IQueuedOperation<TKey, typeof getItemsOperation.type>[];
    workToComplete: Signal<void>[];
  } {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  const workToComplete: Signal<void>[] = [];

  for (let i = 0, length = currentOperations.length; i < length; i++) {
    const currentOperation = currentOperations[i];

    const {
      itemKey,
      completeSignal: currentCompleteSignal,
      options: currentOptions
    } = currentOperation;

    const completedOptions = completedOperations.items[itemKey];

    let remainingOptions: typeof currentOptions;

    if (completedOptions &&
      (remainingOptions = subtract(currentOptions, completedOptions)) &&
      !isSubset(remainingOptions, currentOptions)) {
      // There is a matching completed operation, and subtracing the newly-resolved facets
      // from the original operation has an effect.

      if (!newCurrentOperations) {
        newCurrentOperations = currentOperations.slice(0, i);
      }

      if (Object.keys(remainingOptions).length > 0 && Object.keys(remainingOptions.demands).length > 0) {
        // Add back the part which is still unresolved.
        newCurrentOperations.push({
          ...currentOperation,
          options: remainingOptions
        });
      } else {
        // The current operation is obsolete.
        workToComplete.push(currentCompleteSignal);
      }
    } else {
      // The current operation is untouched.

      if (newCurrentOperations) {
        // Since the list has been modified, ensure the original operation
        // is added back.
        newCurrentOperations.push(currentOperation);
      }
    }
  }

  return {
    newQueue: newCurrentOperations || currentOperations,
    workToComplete: workToComplete
  };
}

function removeUnsupportedFacets<TKey extends object>(
  currentOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[],
  demands: IFaceted<boolean>
): {
    newQueue: IQueuedOperation<TKey, typeof getItemsOperation.type>[];
    workToComplete: Signal<void>[];
  } {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  const workToComplete: Signal<void>[] = [];

  for (let i = 0, length = currentOperations.length; i < length; i++) {
    const currentOperation = currentOperations[0];

    const {
      completeSignal,
      options: {
        demands: currentDemands
      }
    } = currentOperation;

    let newOperation: (typeof currentOperation | undefined) = undefined;

    const filteredDemands = pickObject(currentDemands, demands) as IFaceted<boolean>;

    if (filteredDemands !== currentDemands) {
      newOperation = {
        ...currentOperation,
        options: {
          ...currentOperation.options,
          demands: filteredDemands
        }
      };
    }

    if (newOperation) {
      if (!newCurrentOperations) {
        newCurrentOperations = currentOperations.slice(0, i);
      }

      if (Object.keys(newOperation.options.demands).length > 0) {
        newCurrentOperations.push(newOperation);
      } else {
        // Exclude the operation.
        workToComplete.push(completeSignal);
      }
    } else if (newCurrentOperations) {
      newCurrentOperations.push(currentOperation);
    }
  }

  return {
    newQueue: newCurrentOperations || currentOperations,
    workToComplete: workToComplete
  };
}

/**
 * Produces a new request payload for `getItems` which excludes demands
 * already resolved by the current items in the cache.
 * @internal
 */
export function excludeResolvedDemands(payload: IOperationPayload<typeof getItemsOperation.type>):
  IItemCacheThunkAction<IOperationPayload<typeof getItemsOperation.type>> {
  const {
    items
  } = payload;

  return (dispatch: IItemCacheDispatch): IOperationPayload<typeof getItemsOperation.type> => {
    return {
      items: dispatch(readItems((itemCache: IItemCache) => {
        const remainingItems: typeof items = {};

        for (const itemKey in items) {
          if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
            const resolved = itemCache.demandItemFacet(resolvedFacet, itemKey);

            const keyFacet = KeyFacet.get(itemKey);

            const supportedFacetsItemKey = supportedFacetsItemKeyFacet.serialize({
              keyFacetId: keyFacet.id
            });

            const operationTypeKey = getOperationTypeKey({ operationTypeFacet: getItemsOperation, keyFacet: keyFacet });

            const supportedFacets = itemCache.demandItemFacet(supportedFacetsFacet, supportedFacetsItemKey);

            const supportedDemands = supportedFacets && supportedFacets.demands;

            const {
              demands
            } = items[itemKey];

            const allowedDemands = subtract(
              supportedDemands ? pickObject(demands, supportedDemands) as IFaceted<boolean> : demands,
              {
                ...pack<{}, boolean>({}, {}),
                // resolvedFacet and preferredKeyFacetFacet are 'meta' facets and should never be fetched.
                [resolvedFacet.id]: true,
                [preferredKeyFacetFacet.id]: true
              }
            );

            const resolvedDemands = resolved && mapObject(
              resolved.demands,
              () => true,
              (key: keyof IFaceted<DemandResolution>, value: DemandResolution): keyof IFaceted<boolean> | undefined => {
                if (value === DemandResolution.unhandled) {
                  if (itemCache.demandItemFacet(operationQueueHandlerFacet, operationTypeKey)) {
                    return undefined;
                  }
                }

                return key;
              }) as IFaceted<boolean>;

            let remainingDemands: IFaceted<boolean> | undefined;

            if (Object.keys(allowedDemands).length > 0 &&
              (!resolvedDemands || (
                (remainingDemands = subtract(
                  allowedDemands,
                  resolvedDemands
                )) &&
                remainingDemands && Object.keys(remainingDemands).length > 0
              ))) {
              // If the item is not present in the cache, or does not have a resolved facet,
              // or if there are demands remaining after excluding those already resolved,
              // include the remaining demands, falling back to the original demands.
              remainingItems[itemKey] = {
                demands: remainingDemands || allowedDemands
              };
            } else {
              // The demands are already resolved by the current item in the cache.
            }
          }
        }

        return remainingItems;
      }))
    };
  };
}

function resolveRemainingDemands(payload: IOperationPayload<typeof getItemsOperation.type> & {
  resolution: DemandResolution;
}): IItemCacheThunkAction<void> {
  const {
    items,
    resolution
  } = payload;

  return (dispatch: IItemCacheDispatch) => {
    dispatch(updateItems({
      items: dispatch(readItems((itemCache: IItemCache) => {
        const unresolvedItems: (typeof updateItems.payloadType)['items'] = {};

        for (const itemKey in items) {
          if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
            const {
              demands
            } = items[itemKey];

            const resolved = itemCache.demandItemFacet(resolvedFacet, itemKey);

            const resolvedDemands = resolved && resolved.demands;

            const newResolutions = mapObject(demands, () => resolution, (facetId: keyof typeof demands) => {
              return !resolvedDemands || !resolvedDemands[facetId] ? facetId : undefined;
            });

            unresolvedItems[itemKey] = {
              ...resolvedFacet.pack({
                demands: {
                  ...pack({}, {}),
                  ...newResolutions
                }
              })
            };
          }
        }

        return unresolvedItems;
      }))
    }));
  };
}

/**
 * @public
 */
export interface ISupportFacetsPayload<TKey extends object> {
  keyFacet: KeyFacet<TKey>;
  facets: IFacet<{}>[];
}

/**
 * Creates an action which, when dispatched, specifies that items with the given KeyFacet
 * support only requests which demand at least one of the specified facets.
 *
 * An operation handler backing the specified KeyFacet may resolve more facets on any given item
 * than requested. However, the UX layer will only issue new requests if at least one of the supported
 * facets is in an unresolved state.
 *
 * Use this operation to ensure that operation handlers are not issued spurious requests to resolve
 * facets they can never support.
 * @public
 */
export function supportFacets<TKey extends object>(payload: ISupportFacetsPayload<TKey>): IItemCacheThunkAction<void> {
  const {
    keyFacet,
    facets
  } = payload;

  const itemKey = supportedFacetsItemKeyFacet.serialize({
    keyFacetId: keyFacet.id
  });

  const operationTypeKey = getOperationTypeKey({
    operationTypeFacet: getItemsOperation,
    keyFacet: keyFacet
  });

  const supportedFacets: IFaceted<boolean> = {
    ...pack({}, {}),
    ...makeObject(facets.map((facet: IFacet<{}>) => facet.id), () => true)
  };

  return (dispatch: IItemCacheDispatch) => {
    dispatch(updateItems({
      items: {
        [itemKey]: {
          ...supportedFacetsFacet.pack({
            demands: supportedFacets
          })
        }
      }
    }));

    const operationState = dispatch(getOperationState({
      key: operationTypeKey
    }));

    const queuedOperations = operationState && operationState.queue;

    if (queuedOperations && queuedOperations.length > 0) {
      const {
        newQueue: queuePatchOperation,
        workToComplete
      } = removeUnsupportedFacets(queuedOperations, supportedFacets);

      dispatch(editQueue({
        key: operationTypeKey,
        queue: queuePatchOperation
      }));

      for (const signal of workToComplete) {
        // Mark the work as completed, since future requests would just be a no-op.
        signal.resolve();
      }
    }
  };
}

/**
 * Applies the given item updates to the cache and marks every mentioned
 * facet as resolved.
 * @public
 */
export const resolveItems: IAsyncActionCreator<typeof updateItems.payloadType, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: typeof updateItems.payloadType): IItemCacheThunkAction<void> => {
    const {
      items
    } = payload;

    return (dispatch: IItemCacheDispatch) => {
      const completedOperationsByKey: {
        [operationTypeKey: string]: IOperationPayload<typeof getItemsOperation.type>;
      } = {};

      const resolvedItems: {
        [itemKey: string]: IFaceted;
      } = {};

      for (const itemKey in items) {
        if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
          const keyFacet = KeyFacet.get(itemKey);

          const operationTypeKey = getOperationTypeKey({
            operationTypeFacet: getItemsOperation,
            keyFacet: keyFacet
          });

          const patch = items[itemKey];

          const options: typeof getItemsOperation.type = {
            // Note: the value provided for any facet could be a patch operation,
            // including one which deletes the facet. However, for the moment,
            // consider any operation for a specific facet to be a resolution of that facet.
            demands: patch && typeof patch === 'object' ?
              mapObject(<IFaceted>patch, () => true) :
              pack({}, {})
          };

          resolvedItems[itemKey] = resolvedFacet.pack({
            demands: mapObject(options.demands, () => DemandResolution.resolved)
          });

          let completedOperation = completedOperationsByKey[operationTypeKey];

          if (!completedOperation) {
            completedOperationsByKey[operationTypeKey] = completedOperation = {
              items: {}
            };
          }

          completedOperation.items[itemKey] = options;
        }
      }

      // TODO Combine these.
      // For the moment, resolution of facets needs to be committed before
      // the new values for the facets. This prevents aggressive store
      // subscriptions from re-queuing actions in response to the updates.
      dispatch(updateItems({
        items: resolvedItems
      }));
      dispatch(updateItems(payload));

      for (const operationTypeKey of <OperationTypeKey<object, typeof getItemsOperation.type>[]>Object.keys(completedOperationsByKey)) {
        const completedOperation = completedOperationsByKey[operationTypeKey];

        const operationState = dispatch(getOperationState({
          key: operationTypeKey
        }));

        const queuedOperations = operationState && operationState.queue;

        if (queuedOperations) {
          const {
            newQueue: queuePatchOperation,
            workToComplete
          } = cleanupResolvedGetItemsOperations(queuedOperations, completedOperation);

          dispatch(editQueue({
            key: operationTypeKey,
            queue: queuePatchOperation
          }));

          for (const completeSignal of workToComplete) {
            // Next, immediately mark all removed work as completed.
            completeSignal.resolve();
          }
        }
      }
    };
  });

/**
 * Creator for actions which may be dispatched to get items and their specified facets
 * from their backing data sources, only if the demands are not met by the current state
 * of the item cache.
 * @public
 */
export const getItems: IAsyncActionCreator<IOperationPayload<typeof getItemsOperation.type>, IItemCacheThunkAction<Promise<void>>> =
  createAsyncActionCreator((
    payload: IOperationPayload<typeof getItemsOperation.type>
  ): IItemCacheThunkAction<Promise<void>> => {
    return async (dispatch: IItemCacheDispatch) => {
      const {
        items
      } = dispatch(excludeResolvedDemands(payload));

      if (Object.keys(items).length > 0) {
        const {
          started,
          completed,
          items: itemResults
        } = dispatch(startOperation({
          operationTypeFacet: getItemsOperation,
          enqueueOperations: enqueueGetItemsOperations,
          items: items
        }));

        for (const itemKey in itemResults) {
          if (Object.prototype.hasOwnProperty.call(itemResults, itemKey)) {
            // Track individual item requests to handle errors
            // and defend against repeat requests for the same data.
            dispatch(trackItemRequest({
              itemKey,
              options: items[itemKey],
              ...itemResults[itemKey]
            }));
          }
        }

        try {
          await started;
          await completed;
        } catch (e) {
          if (isUnhandledOperationError(e)) {
            dispatch(handleError({
              error: e
            }));

            return;
          }

          throw e;
        }
      }
    };
  });

function trackItemRequest(payload: {
  itemKey: string;
  options: typeof getItemsOperation.type;
  started: Promise<void>;
  completed: Promise<void>;
}): IItemCacheThunkAction<Promise<void>> {
  return async (dispatch: IItemCacheDispatch) => {
    try {
      await payload.started;
      await payload.completed;

      dispatch(resolveRemainingDemands({
        items: {
          [payload.itemKey]: payload.options
        },
        resolution: DemandResolution.unsupported
      }));
    } catch (e) {
      const isUnhandled = isUnhandledOperationError(e);

      if (!isUnhandled) {
        dispatch(resolveItems({
          items: {
            [payload.itemKey]: {
              ...errorFacet.pack(e)
            }
          }
        }));
      }

      dispatch(resolveRemainingDemands({
        items: {
          [payload.itemKey]: payload.options
        },
        resolution: isUnhandled ? DemandResolution.unhandled : DemandResolution.failed
      }));
    }
  };
}

const getGetItemsBatcher = asOnceOnly((dispatch: IItemCacheDispatch) => {
  let nextTrain: IOperationPayload<typeof getItemsOperation.type> = { items: {} };

  let scheduled: number | undefined;

  function depart(): void {
    const train = nextTrain;

    nextTrain = { items: {} };
    scheduled = undefined;

    dispatch(getItems(train)).catch(() => {
      // Ignore error.
    });
  }

  return (payload: IOperationPayload<typeof getItemsOperation.type>) => {
    for (const itemKey in payload.items) {
      if (Object.prototype.hasOwnProperty.call(payload.items, itemKey)) {
        const options = payload.items[itemKey];

        const item = nextTrain.items[itemKey] || (nextTrain.items[itemKey] = { demands: pack({}, {}) });

        assign(item.demands, options.demands);
      }
    }

    if (!scheduled) {
      scheduled = setTimeout(depart, 0);
    }
  };
});

/**
 * @internal
 */
export function batchedGetItems(payload: IOperationPayload<typeof getItemsOperation.type>): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    const getItemsBatcher = dispatch(getGetItemsBatcher);

    getItemsBatcher(payload);
  };
}
